import { forwardRef, type ButtonHTMLAttributes } from "react";

type Variant = "primary" | "secondary" | "ghost" | "dark" | "danger";
type Size = "sm" | "md" | "lg";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

const variants: Record<Variant, string> = {
  primary:   "bg-fh-green text-white hover:bg-fh-green-dark focus-visible:ring-fh-green disabled:bg-fh-gray-light disabled:text-white",
  secondary: "bg-white text-fh-ink border border-fh-border hover:bg-fh-cream focus-visible:ring-fh-green disabled:text-fh-gray-light",
  ghost:     "bg-transparent text-fh-green hover:text-fh-green-dark focus-visible:ring-fh-green disabled:text-fh-gray-light",
  dark:      "bg-fh-ink text-white border border-fh-ink hover:opacity-90 focus-visible:ring-fh-ink",
  danger:    "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500 disabled:bg-fh-gray-light",
};

const sizes: Record<Size, string> = {
  sm: "px-3 py-2 text-sm rounded-[8px]",
  md: "px-4 py-3 text-[15px] rounded-[10px]",
  lg: "px-5 py-3.5 text-[15px] rounded-[10px]",
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = "primary", size = "md", loading, fullWidth, className = "", disabled, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={[
        "inline-flex items-center justify-center gap-2 font-semibold",
        "transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-fh-cream",
        "disabled:cursor-not-allowed",
        fullWidth ? "w-full" : "",
        variants[variant],
        sizes[size],
        className,
      ].join(" ")}
      {...rest}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
        />
      )}
      {children}
    </button>
  );
});
